import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { createProfessionalInvoice } from '../services/invoiceApi';
import { Card } from './Card';
import { Button } from './Button';
import { toast } from 'react-hot-toast';
import api from '../services/api';

interface HoursRegistration {
  id: string;
  employer_id: string;
  datum: string;
  tijd_minuten: number;
  type: string;
  omschrijving?: string;
  aantal_km?: number;
  reistijd_uren?: number;
  parkeerkosten?: number;
  invoiced: boolean;
}

interface CreateInvoiceFormProps {
  employers: any[];
  onSuccess?: (invoiceId: string) => void;
  onCancel: () => void;
}

export const CreateInvoiceForm: React.FC<CreateInvoiceFormProps> = ({
  employers,
  onSuccess,
  onCancel,
}) => {
  const { t } = useTranslation('pages');
  const queryClient = useQueryClient();
  const [selectedEmployer, setSelectedEmployer] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedHours, setSelectedHours] = useState<string[]>([]);

  const { data: hoursRegistrations } = useQuery({
    queryKey: ['hours-registrations', selectedEmployer, selectedMonth, selectedYear],
    queryFn: async () => {
      const params: Record<string, string | number> = {};
      if (selectedEmployer) params.employer_id = selectedEmployer;
      if (selectedMonth) params.month = selectedMonth;
      if (selectedYear) params.year = selectedYear;

      const response = await api.get('/professional/hours', { params });
      return response.data.data || [];
    },
    enabled: !!selectedEmployer,
  });

  // Agenda-uren lopen via de poolfactuur en mijlpaaluren via Check & Fiat; de server weigert ze hier.
  const availableHours =
    hoursRegistrations?.filter((hr: any) => !hr.invoiced && hr.bron !== 'agenda' && !hr.milestone_id) || [];

  const createInvoiceMutation = useMutation({
    mutationFn: async (data: {
      employer_id: string;
      hours_registration_ids: string[];
      period_start: string;
      period_end: string;
    }) => {
      const res = await createProfessionalInvoice(data);
      return res.data;
    },
    onSuccess: (data: any) => {
      toast.success(t('createInvoice.successToast'));
      queryClient.invalidateQueries({ queryKey: ['professional-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['hours-registrations'] });
      queryClient.invalidateQueries({ queryKey: ['hours-registrations-recent'] });
      onSuccess?.(data?.data?.id);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('createInvoice.errorToast'));
    },
  });

  const handleCreateInvoice = () => {
    if (!selectedEmployer || selectedHours.length === 0) {
      toast.error(t('createInvoice.validationError'));
      return;
    }

    const selectedRegistrations = availableHours.filter((hr: HoursRegistration) =>
      selectedHours.includes(hr.id)
    );

    if (selectedRegistrations.length === 0) {
      toast.error(t('createInvoice.noValidHours'));
      return;
    }

    const dates = selectedRegistrations.map((hr: HoursRegistration) => new Date(hr.datum));
    const periodStart = new Date(Math.min(...dates.map((d: Date) => d.getTime())));
    const periodEnd = new Date(Math.max(...dates.map((d: Date) => d.getTime())));

    createInvoiceMutation.mutate({
      employer_id: selectedEmployer,
      hours_registration_ids: selectedHours,
      period_start: periodStart.toISOString().split('T')[0],
      period_end: periodEnd.toISOString().split('T')[0],
    });
  };

  const toggleHourSelection = (hourId: string) => {
    setSelectedHours((prev) =>
      prev.includes(hourId) ? prev.filter((id) => id !== hourId) : [...prev, hourId]
    );
  };

  const selectAllHours = () => {
    setSelectedHours(availableHours.map((hr: HoursRegistration) => hr.id));
  };

  const deselectAllHours = () => {
    setSelectedHours([]);
  };

  const handleCancel = () => {
    setSelectedEmployer('');
    setSelectedHours([]);
    onCancel();
  };

  return (
    <Card className="mb-6">
      <div className="p-6">
        <h2 className="text-xl font-semibold mb-4">{t('createInvoice.title')}</h2>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('createInvoice.employer')}
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                value={selectedEmployer}
                onChange={(e) => {
                  setSelectedEmployer(e.target.value);
                  setSelectedHours([]);
                }}
              >
                <option value="">{t('createInvoice.selectEmployer')}</option>
                {employers?.map((emp: any) => (
                  <option
                    key={emp.employer_user_id || emp.employer_id}
                    value={emp.employer_user_id || emp.employer_id}
                  >
                    {emp.employer_first_name} {emp.employer_last_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('createInvoice.month')}</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(parseInt(e.target.value));
                  setSelectedHours([]);
                }}
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('createInvoice.year')}</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(parseInt(e.target.value));
                  setSelectedHours([]);
                }}
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(
                  (y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  )
                )}
              </select>
            </div>
          </div>

          {selectedEmployer && availableHours.length > 0 && (
            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">
                  {t('createInvoice.availableHours', { count: availableHours.length })}
                </h3>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={selectAllHours}>
                    {t('createInvoice.selectAll')}
                  </Button>
                  <Button variant="outline" size="sm" onClick={deselectAllHours}>
                    {t('createInvoice.deselectAll')}
                  </Button>
                </div>
              </div>

              <div className="max-h-96 overflow-y-auto border rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-12">
                        <input
                          type="checkbox"
                          checked={
                            selectedHours.length === availableHours.length &&
                            availableHours.length > 0
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              selectAllHours();
                            } else {
                              deselectAllHours();
                            }
                          }}
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('createInvoice.colDate')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('createInvoice.colType')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('createInvoice.colTime')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('createInvoice.colExpenses')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {availableHours.map((hr: HoursRegistration) => (
                      <tr
                        key={hr.id}
                        className={selectedHours.includes(hr.id) ? 'bg-blue-50' : ''}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedHours.includes(hr.id)}
                            onChange={() => toggleHourSelection(hr.id)}
                          />
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {new Date(hr.datum).toLocaleDateString('nl-NL')}
                        </td>
                        <td className="px-4 py-3 text-sm">{hr.type}</td>
                        <td className="px-4 py-3 text-sm">{hr.tijd_minuten} min</td>
                        <td className="px-4 py-3 text-sm">
                          {hr.aantal_km ? t('createInvoice.km', { count: hr.aantal_km }) + ', ' : ''}
                          {hr.reistijd_uren ? t('createInvoice.travelTime', { hours: hr.reistijd_uren }) + ', ' : ''}
                          {hr.parkeerkosten ? t('createInvoice.parking', { amount: hr.parkeerkosten }) : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex justify-end gap-3">
                <Button variant="outline" onClick={handleCancel}>
                  {t('createInvoice.cancel')}
                </Button>
                <Button
                  onClick={handleCreateInvoice}
                  disabled={
                    selectedHours.length === 0 || createInvoiceMutation.isPending
                  }
                >
                  {createInvoiceMutation.isPending
                    ? t('createInvoice.creating')
                    : t('createInvoice.create', { count: selectedHours.length })}
                </Button>
              </div>
            </div>
          )}

          {selectedEmployer && availableHours.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {t('createInvoice.noHours')}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
